/**
 * Training Campaign Detail Page
 * GitHub Issue #285
 *
 * View and manage a training campaign, including assignments and progress.
 */

import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  api,
  TrainingCampaign,
  TrainingAssignment,
  TrainingAssignmentStatus,
} from '../lib/api'
import { useToast } from '../components/Toast'
import { useConfirm } from '../components/ConfirmModal'
import { useAuth } from '../hooks/useAuth'
import {
  ArrowLeft,
  Play,
  Pause,
  CheckCircle2,
  Clock,
  AlertCircle,
  BookOpen,
  Users,
  Calendar,
  Edit2,
  Save,
  X,
  Search,
  Filter,
  Download,
  Send,
  XCircle,
  BarChart3,
  TrendingUp,
} from 'lucide-react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts'

const statusColors: Record<TrainingAssignmentStatus, string> = {
  assigned: 'bg-gray-100 text-gray-700',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  overdue: 'bg-red-100 text-red-700',
  excused: 'bg-yellow-100 text-yellow-700',
}

const CHART_COLORS = ['#22c55e', '#3b82f6', '#6b7280', '#ef4444', '#eab308']

export default function TrainingCampaignDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const toast = useToast()
  const confirmDialog = useConfirm()
  const { user } = useAuth()

  // State
  const [loading, setLoading] = useState(true)
  const [campaign, setCampaign] = useState<TrainingCampaign | null>(null)
  const [assignments, setAssignments] = useState<TrainingAssignment[]>([])
  const [pagination, setPagination] = useState({ total: 0, limit: 20, offset: 0 })

  // Filters
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<TrainingAssignmentStatus | ''>('')

  // Edit state
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    due_days_from_assignment: 14,
  })

  // Fetch campaign
  const fetchCampaign = useCallback(async () => {
    if (!id) return
    try {
      const response = await api.getTrainingCampaign(id)
      if (response.success) {
        setCampaign(response.data)
        setEditForm({
          name: response.data.name,
          description: response.data.description || '',
          due_days_from_assignment: response.data.due_days_from_assignment,
        })
      }
    } catch (error) {
      console.error('Failed to fetch campaign:', error)
      toast.error('Failed to load campaign')
    }
  }, [id, toast])

  // Fetch assignments
  const fetchAssignments = useCallback(async () => {
    if (!id) return
    try {
      const params: any = { limit: pagination.limit, offset: pagination.offset }
      if (searchTerm) params.search = searchTerm
      if (statusFilter) params.status = statusFilter

      const response = await api.getTrainingCampaignAssignments(id, params)
      if (response.success) {
        setAssignments(response.data)
        setPagination(response.pagination)
      }
    } catch (error) {
      console.error('Failed to fetch assignments:', error)
    }
  }, [id, searchTerm, statusFilter, pagination.limit, pagination.offset])

  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      await fetchCampaign()
      setLoading(false)
    }
    loadData()
  }, [fetchCampaign])

  useEffect(() => {
    if (campaign) {
      fetchAssignments()
    }
  }, [campaign, fetchAssignments])

  // Save campaign edits
  const handleSave = async () => {
    if (!campaign) return
    try {
      const response = await api.updateTrainingCampaign(campaign.id, editForm)
      if (response.success) {
        toast.success('Campaign updated')
        setCampaign(response.data)
        setIsEditing(false)
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to update campaign')
    }
  }

  // Launch campaign
  const handleLaunch = async () => {
    if (!campaign) return
    const confirmed = await confirmDialog({
      title: 'Launch Campaign',
      message: `Are you sure you want to launch "${campaign.name}"? This will create assignments for all targeted users.`,
      confirmText: 'Launch',
      type: 'warning',
    })
    if (confirmed) {
      try {
        const response = await api.launchTrainingCampaign(campaign.id)
        if (response.success) {
          toast.success(`Campaign launched! ${response.assignments_created} assignments created.`)
          fetchCampaign()
          fetchAssignments()
        }
      } catch (error: any) {
        toast.error(error.message || 'Failed to launch campaign')
      }
    }
  }

  // Cancel campaign
  const handleCancel = async () => {
    if (!campaign) return
    const confirmed = await confirmDialog({
      title: 'Cancel Campaign',
      message: `Are you sure you want to cancel "${campaign.name}"?`,
      confirmText: 'Cancel Campaign',
      type: 'danger',
    })
    if (confirmed) {
      try {
        const response = await api.cancelTrainingCampaign(campaign.id)
        if (response.success) {
          toast.success('Campaign cancelled')
          fetchCampaign()
        }
      } catch (error: any) {
        toast.error(error.message || 'Failed to cancel campaign')
      }
    }
  }

  // Excuse user
  const handleExcuse = async (assignment: TrainingAssignment) => {
    const reason = prompt('Enter reason for excusing this user:')
    if (!reason) return
    try {
      const response = await api.excuseTrainingAssignment(assignment.id, reason)
      if (response.success) {
        toast.success('User excused from training')
        fetchAssignments()
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to excuse user')
    }
  }

  // Prepare chart data
  const getStatusChartData = () => {
    if (!campaign?.stats) return []
    return [
      { name: 'Completed', value: campaign.stats.completed, color: CHART_COLORS[0] },
      { name: 'In Progress', value: campaign.stats.in_progress, color: CHART_COLORS[1] },
      { name: 'Not Started', value: campaign.stats.not_started, color: CHART_COLORS[2] },
      { name: 'Overdue', value: campaign.stats.overdue, color: CHART_COLORS[3] },
    ].filter(d => d.value > 0)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!campaign) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Campaign not found</h2>
        <p className="text-gray-500 mb-4">The training campaign you're looking for doesn't exist.</p>
        <Link to="/training" className="btn btn-primary">
          Back to Training
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/training')}
            className="btn btn-secondary"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              {isEditing ? (
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="input text-xl font-bold"
                />
              ) : (
                <h1 className="text-2xl font-bold text-gray-900">{campaign.name}</h1>
              )}
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                campaign.status === 'active' ? 'bg-blue-100 text-blue-700' :
                campaign.status === 'draft' ? 'bg-gray-100 text-gray-700' :
                campaign.status === 'completed' ? 'bg-green-100 text-green-700' :
                'bg-red-100 text-red-700'
              }`}>
                {campaign.status}
              </span>
            </div>
            {isEditing ? (
              <textarea
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                className="input w-full mt-2"
                rows={2}
                placeholder="Campaign description..."
              />
            ) : (
              campaign.description && (
                <p className="text-gray-500 mt-1">{campaign.description}</p>
              )
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {campaign.status === 'draft' && (
            <>
              {isEditing ? (
                <>
                  <button
                    onClick={() => {
                      setIsEditing(false)
                      setEditForm({
                        name: campaign.name,
                        description: campaign.description || '',
                        due_days_from_assignment: campaign.due_days_from_assignment,
                      })
                    }}
                    className="btn btn-secondary"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="btn btn-primary"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="btn btn-secondary"
                  >
                    <Edit2 className="h-4 w-4 mr-2" />
                    Edit
                  </button>
                  <button
                    onClick={handleLaunch}
                    className="btn btn-primary"
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Launch Campaign
                  </button>
                </>
              )}
            </>
          )}
          {campaign.status === 'active' && (
            <button
              onClick={handleCancel}
              className="btn btn-secondary text-red-600"
            >
              <XCircle className="h-4 w-4 mr-2" />
              Cancel Campaign
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      {campaign.stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-500 text-sm">Total Assigned</span>
              <Users className="h-5 w-5 text-gray-400" />
            </div>
            <p className="text-2xl font-bold mt-2">{campaign.stats.total_assignments}</p>
          </div>
          <div className="bg-green-50 rounded-lg border border-green-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-green-700 text-sm">Completed</span>
              <CheckCircle2 className="h-5 w-5 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-green-700 mt-2">{campaign.stats.completed}</p>
          </div>
          <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-blue-700 text-sm">In Progress</span>
              <TrendingUp className="h-5 w-5 text-blue-500" />
            </div>
            <p className="text-2xl font-bold text-blue-700 mt-2">{campaign.stats.in_progress}</p>
          </div>
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-700 text-sm">Not Started</span>
              <Clock className="h-5 w-5 text-gray-500" />
            </div>
            <p className="text-2xl font-bold text-gray-700 mt-2">{campaign.stats.not_started}</p>
          </div>
          <div className="bg-red-50 rounded-lg border border-red-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-red-700 text-sm">Overdue</span>
              <AlertCircle className="h-5 w-5 text-red-500" />
            </div>
            <p className="text-2xl font-bold text-red-700 mt-2">{campaign.stats.overdue}</p>
          </div>
        </div>
      )}

      {/* Progress Chart and Modules */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Progress Chart */}
        {campaign.stats && campaign.stats.total_assignments > 0 && (
          <div className="bg-white rounded-lg border p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Progress Overview</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={getStatusChartData()}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {getStatusChartData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap justify-center gap-4 mt-4">
              {getStatusChartData().map((entry) => (
                <div key={entry.name} className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-sm text-gray-600">{entry.name}: {entry.value}</span>
                </div>
              ))}
            </div>
            <div className="text-center mt-4">
              <p className="text-2xl font-bold text-primary-600">
                {campaign.stats.completion_rate.toFixed(1)}%
              </p>
              <p className="text-sm text-gray-500">Completion Rate</p>
            </div>
          </div>
        )}

        {/* Modules */}
        <div className="bg-white rounded-lg border p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Included Modules</h3>
          {campaign.modules && campaign.modules.length > 0 ? (
            <div className="space-y-3">
              {campaign.modules.map((module, idx) => (
                <div
                  key={module.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-400">{idx + 1}.</span>
                    <div>
                      <Link
                        to={`/training/modules/${module.id}`}
                        className="font-medium text-gray-900 hover:text-primary-600"
                      >
                        {module.title}
                      </Link>
                      <p className="text-sm text-gray-500">
                        {module.estimated_minutes} min | {module.content_type}
                      </p>
                    </div>
                  </div>
                  {module.is_mandatory && (
                    <span className="text-xs text-red-600">Mandatory</span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <BookOpen className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p>No modules in this campaign</p>
            </div>
          )}
        </div>
      </div>

      {/* Assignments Table */}
      <div className="bg-white rounded-lg border">
        <div className="p-4 border-b flex flex-col md:flex-row md:items-center justify-between gap-4">
          <h3 className="font-semibold text-gray-900">Assignments</h3>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10 w-64"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as TrainingAssignmentStatus | '')}
              className="input"
            >
              <option value="">All Statuses</option>
              <option value="assigned">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="overdue">Overdue</option>
              <option value="excused">Excused</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {assignments.map((assignment) => (
                <tr key={assignment.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <p className="font-medium text-gray-900">{assignment.user_username}</p>
                      <p className="text-sm text-gray-500">{assignment.user_email}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusColors[assignment.status]}`}>
                      {assignment.status === 'in_progress' ? 'In Progress' : assignment.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            assignment.status === 'completed' ? 'bg-green-500' :
                            assignment.status === 'overdue' ? 'bg-red-500' : 'bg-blue-500'
                          }`}
                          style={{ width: `${assignment.overall_progress_percent}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-600">{assignment.overall_progress_percent}%</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {assignment.modules_completed}/{assignment.modules_total} modules
                    </p>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {assignment.due_date ? (
                      <span className={`text-sm ${
                        assignment.status === 'overdue' ? 'text-red-600' : 'text-gray-600'
                      }`}>
                        {new Date(assignment.due_date).toLocaleDateString()}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {assignment.overall_score !== null ? (
                      <span className={`text-sm font-medium ${
                        assignment.overall_score >= 70 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {assignment.overall_score}%
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    {assignment.status !== 'completed' && assignment.status !== 'excused' && (
                      <button
                        onClick={() => handleExcuse(assignment)}
                        className="text-sm text-gray-600 hover:text-gray-900"
                      >
                        Excuse
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {assignments.length === 0 && (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No assignments found</p>
              {campaign.status === 'draft' && (
                <p className="text-sm text-gray-400 mt-1">
                  Launch the campaign to create assignments
                </p>
              )}
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.total > pagination.limit && (
          <div className="px-6 py-4 border-t flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Showing {pagination.offset + 1} to {Math.min(pagination.offset + assignments.length, pagination.total)} of {pagination.total}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination(p => ({ ...p, offset: Math.max(0, p.offset - p.limit) }))}
                disabled={pagination.offset === 0}
                className="btn btn-sm btn-secondary disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(p => ({ ...p, offset: p.offset + p.limit }))}
                disabled={pagination.offset + assignments.length >= pagination.total}
                className="btn btn-sm btn-secondary disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
